/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { riskCustomRulesSuggest } from "../funcs/riskCustomRulesSuggest.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SuggestCustomDetectionRuleResult } from "../models/components/suggestcustomdetectionruleresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SuggestCustomDetectionRuleRequest,
  SuggestCustomDetectionRuleSecurity,
} from "../models/operations/suggestcustomdetectionrule.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RiskSuggestCustomRuleMutationVariables = {
  request: SuggestCustomDetectionRuleRequest;
  security?: SuggestCustomDetectionRuleSecurity | undefined;
  options?: RequestOptions;
};

export type RiskSuggestCustomRuleMutationData =
  SuggestCustomDetectionRuleResult;

export type RiskSuggestCustomRuleMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * suggestCustomDetectionRule risk
 *
 * @remarks
 * Suggest a custom detection rule (rule_id, title, description, regex, severity) from a natural-language prompt. Calls the configured LLM with a JSON-schema constrained response so the dashboard can prefill the create form.
 */
export function useRiskSuggestCustomRuleMutation(
  options?: MutationHookOptions<
    RiskSuggestCustomRuleMutationData,
    RiskSuggestCustomRuleMutationError,
    RiskSuggestCustomRuleMutationVariables
  >,
): UseMutationResult<
  RiskSuggestCustomRuleMutationData,
  RiskSuggestCustomRuleMutationError,
  RiskSuggestCustomRuleMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRiskSuggestCustomRuleMutation(client, options),
    ...options,
  });
}

export function mutationKeyRiskSuggestCustomRule(): MutationKey {
  return ["@gram/client", "customRules", "suggest"];
}

export function buildRiskSuggestCustomRuleMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RiskSuggestCustomRuleMutationVariables,
  ) => Promise<RiskSuggestCustomRuleMutationData>;
} {
  return {
    mutationKey: mutationKeyRiskSuggestCustomRule(),
    mutationFn: function riskSuggestCustomRuleMutationFn({
      request,
      security,
      options,
    }): Promise<RiskSuggestCustomRuleMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(riskCustomRulesSuggest(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
